import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { IAddress, IEventHotel } from "@eagerdog/interfaces";
import { Config } from "@eagerdog/config";

import { helperService } from "src/services/helper.service";

import "./EventMapComponent.scss";

interface IProps {
  address?: IAddress;
  eventTitle?: string;
  hotels?: IEventHotel[];
}

// Declare global google types
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });

// Distinguishes hotel pins from the event pin without needing a custom asset.
const HOTEL_MARKER_ICON = "https://maps.google.com/mapfiles/ms/icons/blue-dot.png";

const EventMapComponent: React.FC<IProps> = ({ address, eventTitle, hotels }) => {
  const [map, setMap] = useState<any>(null);
  const [marker, setMarker] = useState<any>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceId = useRef<string>(Math.random().toString(36).substr(2, 9));
  const hotelMarkersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);

  // Default coordinates (center of US if no location provided)
  const defaultCoords = useMemo(() => ({ lat: 39.8283, lng: -98.5795 }), []);

  const getCoordinates = useCallback(async () => {
    // Try to geocode the address
    if (address && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      const addressString = `${address.line1 || ''} ${address.city || ''} ${address.state || ''} ${address.postal_code || ''}`.trim();

      if (addressString) {
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: addressString }, (results: any, status: any) => {
              if (status === 'OK' && results[0]) {
                resolve({
                  lat: results[0].geometry.location.lat(),
                  lng: results[0].geometry.location.lng()
                });
              } else {
                reject(new Error('Geocoding failed'));
              }
            });
          });
          return result as { lat: number; lng: number };
        } catch (e) {
          console.warn('Geocoding failed, using default coordinates');
        }
      }
    }

    return defaultCoords;
  }, [address, defaultCoords]);

  // Resolves a hotel's coordinates — prefers coordinates already saved on the
  // hotel (set when it was added/edited) and only falls back to geocoding on
  // the fly for hotels saved before this field existed.
  const getHotelCoordinates = useCallback(async (hotel: IEventHotel): Promise<{ lat: number; lng: number } | undefined> => {
    if (hotel.coordinates) {
      return hotel.coordinates;
    }

    if (!window.google || !hotel.address) {
      return undefined;
    }

    const addressString = helperService.formatAddress(hotel.address).replace(/,\s*$/, "");
    if (!addressString) {
      return undefined;
    }

    const geocoder = new window.google.maps.Geocoder();
    try {
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: addressString }, (results: any, status: any) => {
          if (status === 'OK' && results[0]) {
            resolve({
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng()
            });
          } else {
            reject(new Error('Geocoding failed'));
          }
        });
      });
      return result as { lat: number; lng: number };
    } catch (e) {
      console.warn(`Could not geocode hotel "${hotel.name}"`);
      return undefined;
    }
  }, []);

  const buildHotelInfoContent = (hotel: IEventHotel): string => {
    const parts: string[] = [`<strong>${escapeHtml(hotel.name)}</strong>`];
    const addressLine = helperService.formatAddress(hotel.address).replace(/,\s*$/, "");
    if (addressLine) parts.push(escapeHtml(addressLine));
    if (hotel.distance_value !== undefined) {
      parts.push(`${hotel.distance_value} ${hotel.distance_unit} from event`);
    }
    if (hotel.has_special_rate && hotel.rate !== undefined && hotel.rate !== null) {
      parts.push(`Special rate: $${hotel.rate.toFixed(2)} / night`);
    }
    const bookUrl = helperService.normalizeUrl(hotel.website_url);
    if (bookUrl) {
      parts.push(`<a href="${escapeHtml(bookUrl)}" target="_blank" rel="noreferrer">Book</a>`);
    }
    return `<div class="EventMapHotelInfo">${parts.join("<br/>")}</div>`;
  };

  // Clears any previously plotted hotel markers so re-renders (e.g. a hotel
  // being added/edited while this page is open) don't leave stale pins behind.
  const clearHotelMarkers = useCallback(() => {
    hotelMarkersRef.current.forEach((m) => {
      window.google?.maps?.event?.clearInstanceListeners?.(m);
      m.setMap(null);
    });
    hotelMarkersRef.current = [];
  }, []);

  const plotHotelMarkers = useCallback(async (targetMap: any, eventCoordinates: { lat: number; lng: number }) => {
    clearHotelMarkers();

    if (!hotels || hotels.length === 0 || !window.google) {
      return;
    }

    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(eventCoordinates);
    let plottedAny = false;

    for (const hotel of hotels) {
      const coordinates = await getHotelCoordinates(hotel);
      if (!coordinates) continue;

      const hotelMarker = new window.google.maps.Marker({
        position: coordinates,
        map: targetMap,
        title: hotel.name,
        icon: HOTEL_MARKER_ICON,
      });

      hotelMarker.addListener("click", () => {
        infoWindowRef.current.setContent(buildHotelInfoContent(hotel));
        infoWindowRef.current.open(targetMap, hotelMarker);
      });

      hotelMarkersRef.current.push(hotelMarker);
      bounds.extend(coordinates);
      plottedAny = true;
    }

    // Zoom/pan to fit the event + all hotel pins, instead of the fixed
    // zoom level used when there's only the single event marker.
    if (plottedAny) {
      targetMap.fitBounds(bounds, 40);
    }
  }, [hotels, getHotelCoordinates, clearHotelMarkers]);

  const initializeMap = useCallback(async () => {
    if (!window.google || !window.google.maps) {
      setError("Google Maps failed to load");
      return;
    }

    // Prevent multiple initializations
    if (map) {
      return;
    }

    try {
      const coordinates = await getCoordinates();
      const mapElement = mapContainerRef.current;

      if (!mapElement) {
        return;
      }

      // Clear any existing content and ensure we have a clean container
      mapElement.innerHTML = '';

      // Wait a tick for React to finish any pending operations
      await new Promise(resolve => setTimeout(resolve, 0));

      const newMap = new window.google.maps.Map(mapElement, {
        center: coordinates,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        gestureHandling: 'cooperative'
      });

      const newMarker = new window.google.maps.Marker({
        position: coordinates,
        map: newMap,
        title: eventTitle || 'Event Location',
        draggable: false
      });

      setMap(newMap);
      setMarker(newMarker);
      setIsLoaded(true);

      await plotHotelMarkers(newMap, coordinates);
    } catch (e) {
      console.error('Map initialization failed:', e);
      setError("Failed to initialize map");
    }
  }, [address, eventTitle, getCoordinates, map, plotHotelMarkers]);

  useEffect(() => {
    let mounted = true;

    const loadMap = async () => {
      // Check if API key is available
      const apiKey = Config.web.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        if (mounted) {
          setError("Google Maps API key not configured");
        }
        return;
      }

      // Check if Google Maps API is already loaded
      if (window.google && window.google.maps) {
        if (mounted) {
          initializeMap();
        }
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        const handleLoad = () => {
          if (mounted) {
            initializeMap();
          }
        };

        const handleError = () => {
          if (mounted) {
            setError("Failed to load Google Maps");
          }
        };

        existingScript.addEventListener('load', handleLoad);
        existingScript.addEventListener('error', handleError);

        return () => {
          existingScript.removeEventListener('load', handleLoad);
          existingScript.removeEventListener('error', handleError);
        };
      }

      // Load Google Maps API
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (mounted) {
          initializeMap();
        }
      };
      script.onerror = () => {
        if (mounted) {
          setError("Failed to load Google Maps API.");
        }
      };
      document.head.appendChild(script);
    };

    loadMap();

    return () => {
      mounted = false;
    };
  }, [initializeMap]);

  // Google Maps sizes its tiles based on the container's dimensions at the
  // moment it's created and doesn't redraw automatically if that size
  // changes later (e.g. the layout switching to its responsive/stacked
  // breakpoint, or the window being resized). Without this, the map is left
  // showing a blank/gray box. Watch the container and nudge Maps to resize.
  useEffect(() => {
    if (!map || !mapContainerRef.current || typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!window.google?.maps) return;
      const currentCenter = map.getCenter();
      window.google.maps.event.trigger(map, "resize");
      if (currentCenter) {
        map.setCenter(currentCenter);
      }
    });

    resizeObserver.observe(mapContainerRef.current);

    return () => resizeObserver.disconnect();
  }, [map]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (map) {
        try {
          // Clear all listeners
          window.google?.maps?.event?.clearInstanceListeners?.(map);
          clearHotelMarkers();

          // Clear the map container completely
          if (mapContainerRef.current) {
            mapContainerRef.current.innerHTML = '';
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [map, clearHotelMarkers]);

  // Additional cleanup on unmount
  useEffect(() => {
    return () => {
      // Force clear the container on component unmount
      if (mapContainerRef.current) {
        try {
          mapContainerRef.current.innerHTML = '';
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  // Update map when address changes
  useEffect(() => {
    const updateMapLocation = async () => {
      if (map && marker && window.google) {
        try {
          const coordinates = await getCoordinates();
          map.setCenter(coordinates);
          marker.setPosition(coordinates);
          map.setZoom(15);
          await plotHotelMarkers(map, coordinates);
        } catch (e) {
          console.warn('Failed to update map location:', e);
        }
      }
    };

    // Only update if we have a map initialized
    if (map && marker) {
      updateMapLocation();
    }
  }, [address, map, marker, getCoordinates, plotHotelMarkers]);

  // Re-plot hotel markers when the hotel list changes (e.g. a hotel was
  // added/edited/removed while this page is open) without re-centering the map.
  useEffect(() => {
    if (map && marker) {
      plotHotelMarkers(map, marker.getPosition().toJSON());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels]);

  if (error) {
    return (
      <div className="EventMapComponent">
        <div className="mapError">
          <div className="icon">🗺️</div>
          <div className="message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="EventMapComponent">
      <div className="mapWrapper">
        <div
          ref={mapContainerRef}
          className="mapContainer"
          key={mapInstanceId.current}
          suppressHydrationWarning={true}
        />
        {!isLoaded && !error && (
          <div className="mapLoading">
            <div className="spinner"></div>
            <div>Loading map...</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventMapComponent;
